import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  FileSpreadsheet, FileText, Download, Database, Info, ArrowRight, Leaf, Users, ShieldCheck,
  Layers, BadgeCheck, UploadCloud, CalendarRange,
} from "lucide-react";

const PILLAR_ICON = { Environment: Leaf, Social: Users, Governance: ShieldCheck };
const CORE_KEYS = ["ghg_data", "esg_records", "kpi_register"];
const _CUR_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [_CUR_YEAR, _CUR_YEAR - 1, _CUR_YEAR - 2, _CUR_YEAR - 3, _CUR_YEAR - 4];

export default function DataCollection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [baseYear, setBaseYear] = useState("");

  useEffect(() => {
    api.get("/templates").then((r) => setData(r.data)).catch(() => toast.error("Failed to load templates"));
  }, []);

  const download = async (key, fmt) => {
    setBusy((b) => ({ ...b, [`${key}-${fmt}`]: true }));
    try {
      const params = { format: fmt };
      if (fmt === "xlsx" && baseYear) params.base_year = baseYear;
      const r = await api.get(`/templates/${key}/download`, { params, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `lestari_${key}.${fmt}`;
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error("Download failed"); }
    finally { setBusy((b) => ({ ...b, [`${key}-${fmt}`]: false })); }
  };

  const groups = useMemo(() => {
    const all = data?.templates || [];
    const core = all.filter((t) => CORE_KEYS.includes(t.key));
    const metrics = all.filter((t) => !CORE_KEYS.includes(t.key));
    const byPillar = { Environment: [], Social: [], Governance: [] };
    metrics.forEach((t) => { (byPillar[t.pillar] || (byPillar[t.pillar] = [])).push(t); });
    return { core, byPillar };
  }, [data]);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-8" data-testid="templates-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow mb-1">Data · Collection Templates</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
          <Database className="h-8 w-8 text-accent" /> Data Collection Templates
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Download standardised CSV / Excel templates for every metric and KPI. Each explains what it is for and where the
          data typically comes from. The version is embedded in the filename so you can confirm a previously downloaded
          template is still valid against the current data model.
        </p>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {data && <Badge variant="outline" className="gap-1 text-xs" data-testid="schema-version"><BadgeCheck className="h-3.5 w-3.5 text-accent" /> Data model schema v{data.schema_version}</Badge>}
          <Link to="/data-management" className="text-xs text-accent hover:underline inline-flex items-center gap-1" data-testid="goto-import-link">
            <UploadCloud className="h-3.5 w-3.5" /> Upload a filled template via AI Import <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="flex items-center gap-2 mt-3 text-xs" data-testid="base-year-control">
          <CalendarRange className="h-3.5 w-3.5 text-accent" />
          <span className="text-muted-foreground">Excel base year:</span>
          <select
            value={baseYear}
            onChange={(e) => setBaseYear(e.target.value)}
            data-testid="base-year-select"
            className="h-7 rounded-sm border border-border bg-surface px-2 text-xs focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">No pre-fill</option>
            {YEAR_OPTIONS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </select>
          <span className="text-muted-foreground/70">
            {baseYear ? `Excel files pre-fill 12 monthly rows (${baseYear}-01…${baseYear}-12) + annual auto-total` : "Pick a year to pre-fill monthly periods in Excel downloads"}
          </span>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-muted-foreground">Loading templates…</div>
      ) : (
        <>
          {/* Core templates */}
          <section>
            <h2 className="text-base font-semibold flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-accent" /> Core data templates</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {groups.core.map((t) => <TemplateCard key={t.key} t={t} onDownload={download} busy={busy} />)}
            </div>
          </section>

          {/* Standard-aligned metric templates by pillar */}
          <section>
            <h2 className="text-base font-semibold flex items-center gap-2 mb-1"><BadgeCheck className="h-4 w-4 text-accent" /> Standard-aligned metric templates</h2>
            <p className="text-xs text-muted-foreground mb-3">Per-metric source-data templates aligned to recognised frameworks (GHG Protocol, IFRS S2, GRI, SASB, CDP, SGX).</p>
            {["Environment", "Social", "Governance"].map((pillar) => {
              const list = groups.byPillar[pillar] || [];
              if (!list.length) return null;
              const PIcon = PILLAR_ICON[pillar];
              return (
                <div key={pillar} className="mb-5">
                  <div className="flex items-center gap-2 mb-2 text-sm font-medium text-muted-foreground"><PIcon className="h-4 w-4" /> {pillar}</div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {list.map((t) => <TemplateCard key={t.key} t={t} onDownload={download} busy={busy} />)}
                  </div>
                </div>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}

function TemplateCard({ t, onDownload, busy }) {
  return (
    <div className="card-flat p-4 flex flex-col gap-3" data-testid="template-card">
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm leading-tight">{t.name}</h3>
          <Badge variant="outline" className="text-[9px] shrink-0 font-mono" data-testid={`version-${t.key}`}>v{t.version}</Badge>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {(t.standards || []).map((s) => <Badge key={s} variant="outline" className="text-[9px] py-0">{s}</Badge>)}
        </div>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>

      <div className="rounded-sm bg-surface-hover/50 border border-border p-2.5">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground mb-1"><Info className="h-3 w-3" /> Where this data comes from</div>
        <p className="text-[11px] text-foreground/90 leading-relaxed">{t.data_source}</p>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Columns ({t.columns.length})</div>
        <div className="flex flex-wrap gap-1">
          {t.columns.slice(0, 6).map((c) => <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-black/20 border border-border">{c}</span>)}
          {t.columns.length > 6 && <span className="text-[10px] text-muted-foreground">+{t.columns.length - 6} more</span>}
        </div>
      </div>

      <div className="flex gap-2 mt-auto pt-1">
        <Button variant="outline" size="sm" className="flex-1" disabled={busy[`${t.key}-csv`]} onClick={() => onDownload(t.key, "csv")} data-testid={`download-csv-${t.key}`}>
          <FileText className="h-3.5 w-3.5" /> CSV
        </Button>
        <Button variant="primary" size="sm" className="flex-1" disabled={busy[`${t.key}-xlsx`]} onClick={() => onDownload(t.key, "xlsx")} data-testid={`download-xlsx-${t.key}`}>
          <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
        </Button>
      </div>
    </div>
  );
}
